import { useState } from 'react';
import { ExternalLink, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, formatEur, inputCls, statusBadge } from '../../utils/operations';

const NEXT_ACTIONS = {
  draft: [{ status: 'review', label: 'Enviar para review' }],
  review: [{ status: 'approved', label: 'Aprovar' }, { status: 'rejected', label: 'Rejeitar' }],
  approved: [{ status: 'published', label: 'Publicar' }, { status: 'review', label: 'Devolver a review' }],
  published: [{ status: 'approved', label: 'Despublicar' }],
  rejected: [{ status: 'draft', label: 'Voltar a draft' }],
};

const ProductRow = ({ product, onRefresh }) => {
  const [margin, setMargin] = useState(product.margin_pct ?? '');
  const [notes, setNotes] = useState(product.review_notes || '');
  const [approvedPrice, setApprovedPrice] = useState(Boolean(product.price_approved));
  const [busy, setBusy] = useState(false);

  const patch = async (payload, success = 'Produto atualizado') => {
    setBusy(true);
    try {
      await apiFetch(`/products/${product.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      toast.success(success);
      await onRefresh();
    } catch (err) {
      toast.error('Produto não atualizado', { description: String(err.message) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <article data-testid={`admin-product-${product.id}`} className="rounded-xl border border-eco-ink/10 bg-white p-5">
      <div className="flex flex-wrap gap-4">
        {product.images?.[0] ? <img src={`${process.env.REACT_APP_BACKEND_URL}${product.images[0]}`} alt="" className="h-20 w-28 rounded-xl object-cover" /> : <div className="grid h-20 w-28 place-items-center rounded-xl bg-eco-cream text-xs text-eco-ink/35">Sem imagem</div>}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">{product.name}</p>
            <span data-testid={`admin-product-status-${product.id}`} className={`rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(product.status)}`}>{product.status}</span>
          </div>
          <p className="mt-1 text-xs text-eco-ink/55">{product.type} · {product.partner_name} · {product.island}{product.country && `, ${product.country}`}</p>
          <p className="mt-2 line-clamp-2 text-sm text-eco-ink/65">{product.description}</p>
        </div>
        {product.status === 'published' && <a data-testid={`admin-product-public-${product.id}`} href={`/partner-products/${product.id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 self-start text-xs font-semibold text-eco-gold"><ExternalLink size={13} /> Público</a>}
      </div>

      <div className="mt-4 grid gap-3 border-t border-eco-ink/10 pt-4 md:grid-cols-[130px_150px_130px_1fr_auto]">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">Net parceiro</p>
          <p data-testid={`admin-product-net-${product.id}`} className="eco-number mt-1 text-sm font-semibold text-eco-ink">{formatEur(product.price_net_eur)}</p>
        </div>
        <label>
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">Pricing mode</span>
          <select data-testid={`admin-product-pricing-mode-${product.id}`} value={product.pricing_mode || 'on_request'} onChange={(e) => patch({ pricing_mode: e.target.value }, 'Pricing mode atualizado')} className={`${inputCls} mt-1`}>
            <option value="on_request">Price on request</option>
            <option value="from_price">From price</option>
            <option value="fixed_price">Fixed price</option>
            <option value="custom_proposal">Custom proposal</option>
          </select>
        </label>
        <label>
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">Margem %</span>
          <input data-testid={`admin-product-margin-${product.id}`} type="number" min="0" max="80" step="0.1" value={margin} onChange={(e) => setMargin(e.target.value)} className={`${inputCls} mt-1`} />
        </label>
        <label>
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-eco-ink/40">Notas de moderação</span>
          <input data-testid={`admin-product-notes-${product.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} className={`${inputCls} mt-1`} placeholder="Notas internas para o parceiro" />
        </label>
        <div className="flex items-end gap-2">
          <button data-testid={`admin-product-save-${product.id}`} onClick={() => patch({ margin_pct: margin === '' ? null : Number(margin), price_approved: approvedPrice, review_notes: notes })} disabled={busy} className="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-eco-green/30 px-4 text-xs font-semibold text-eco-green transition-colors hover:bg-eco-green/10"><Save size={13} /> Guardar</button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
        <span className="text-eco-ink/50">Preço calculado: <strong className="text-eco-gold">{formatEur(product.price_public_eur)}</strong></span>
        <label className="flex items-center gap-2 font-medium text-eco-ink/65">
          <input data-testid={`admin-product-price-approved-${product.id}`} type="checkbox" checked={approvedPrice} onChange={(e) => setApprovedPrice(e.target.checked)} className="h-4 w-4 accent-eco-green" />
          Preço público aprovado
        </label>
        <span className="text-eco-ink/40">{approvedPrice ? 'Público verá o preço' : 'Público vê “Price on request”'}</span>
        {busy && <Loader2 size={14} className="animate-spin text-eco-gold" />}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {(NEXT_ACTIONS[product.status] || []).map((action) => (
          <button key={action.status} data-testid={`admin-product-${action.status}-${product.id}`} onClick={() => patch({ status: action.status, margin_pct: margin === '' ? null : Number(margin), price_approved: approvedPrice, review_notes: notes }, `Produto movido para ${action.status}`)} disabled={busy} className={`inline-flex min-h-10 items-center rounded-lg px-4 text-xs font-semibold transition-colors ${action.status === 'rejected' ? 'border border-red-200 text-red-600 hover:bg-red-50' : 'bg-eco-green text-white hover:bg-eco-deep'}`}>{action.label}</button>
        ))}
      </div>
    </article>
  );
};

export const AdminProducts = ({ products, onRefresh }) => {
  const [filter, setFilter] = useState('all');
  const visible = products.filter((p) => filter === 'all' || p.status === filter);
  return (
    <div data-testid="admin-products-panel">
      <div className="flex flex-wrap gap-2" data-testid="admin-product-filters">
        {['all', 'draft', 'review', 'approved', 'published', 'rejected'].map((s) => (
          <button key={s} data-testid={`admin-product-filter-${s}`} onClick={() => setFilter(s)} className={`rounded-full px-4 py-1.5 text-xs font-semibold ${filter === s ? 'bg-eco-green text-white' : 'bg-white text-eco-ink/60'}`}>{s} ({products.filter((p) => s === 'all' || p.status === s).length})</button>
        ))}
      </div>
      <div className="mt-6 space-y-4">
        {visible.map((p) => <ProductRow key={p.id} product={p} onRefresh={onRefresh} />)}
        {visible.length === 0 && <p className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-12 text-center text-sm text-eco-ink/50">Sem produtos neste estado.</p>}
      </div>
    </div>
  );
};
